import { Chip } from "@mui/material";
import { addDays, getDay, format, parseISO, startOfWeek, endOfWeek } from "date-fns";
import { pt } from "date-fns/locale";
import { InvoiceSchedule, OrderItemForScheduling, ScheduledInvoiceItem } from "../types";

export const DAYS_OF_WEEK = [
    { value: 1, label: "Segunda-feira" },
    { value: 2, label: "Terça-feira" },
    { value: 3, label: "Quarta-feira" },
    { value: 4, label: "Quinta-feira" },
    { value: 5, label: "Sexta-feira" },
    { value: 6, label: "Sábado" },
];

export const calculateBulkDates = (
    firstDate: Date,
    count: number,
    frequency: "único" | "daily" | "weekly",
    selectedDays: Array<number>
): Array<Date> => {
    if (frequency === "único") {
        return [firstDate];
    }

    const dates: Array<Date> = [];
    let currentDate = firstDate;

    if (frequency === "daily") {
        for (let i = 0; i < count; i++) {
            dates.push(new Date(currentDate));
            currentDate = addDays(currentDate, 1);
        }
    } else {
        while (dates.length < count) {
            const dayOfWeek = getDay(currentDate);
            if (selectedDays.includes(dayOfWeek)) {
                dates.push(new Date(currentDate));
            }
            currentDate = addDays(currentDate, 1);
        }
    }

    return dates;
};

export const calculateScheduledTotal = (schedules: Array<InvoiceSchedule>) => {
    if (!schedules) return 0;

    const total = schedules.reduce((sum: number, schedule: InvoiceSchedule) => {
        const scheduleTotal =
            schedule.scheduled_invoices_items?.reduce((itemSum, item) => itemSum + item.quantity * item.price, 0) || 0;
        return sum + scheduleTotal;
    }, 0);

    return total;
};

export const isSchedulePending = (schedule: InvoiceSchedule) => {
    const isPending = schedule.status !== "created" && !schedule.invoice_number;
    return isPending;
};

export const calculatePendingScheduledTotal = (schedules: Array<InvoiceSchedule>) => {
    if (!schedules) return 0;

    const pendingSchedules = schedules.filter(isSchedulePending);
    const total = calculateScheduledTotal(pendingSchedules);
    return total;
};

type GroupedSchedules = {
    weekLabel: string;
    schedules: Array<InvoiceSchedule>;
};

export const formatScheduleDateWithDay = (dateString: string) => {
    const formatted = format(new Date(dateString), "EEEE, dd/MM/yyyy", { locale: pt });
    const capitalizedFormatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return capitalizedFormatted;
};

export const formatWeekRange = (date: Date) => {
    const weekStart = startOfWeek(date, { locale: pt });
    const weekEnd = endOfWeek(date, { locale: pt });
    const weekRangeLabel = `Semana ${format(weekStart, "dd/MM", { locale: pt })} - ${format(weekEnd, "dd/MM", { locale: pt })}`;
    return weekRangeLabel;
};

export const groupSchedulesByWeek = (schedules: Array<InvoiceSchedule>) => {
    const grouped = new Map<string, Array<InvoiceSchedule>>();

    schedules.forEach((schedule) => {
        const scheduleDate = new Date(schedule.scheduled_date);
        const weekStart = startOfWeek(scheduleDate, { locale: pt });
        const weekKey = format(weekStart, "yyyy-MM-dd", { locale: pt });

        const existingSchedules = grouped.get(weekKey) || [];
        grouped.set(weekKey, [...existingSchedules, schedule]);
    });

    const groupedArray: Array<GroupedSchedules> = Array.from(grouped.entries()).map(([_, schedules]) => {
        const firstScheduleDate = new Date(schedules[0].scheduled_date);
        const weekLabel = formatWeekRange(firstScheduleDate);
        
        return {
            weekLabel,
            schedules,
        };
    });

    return groupedArray;
};

export const formatScheduledDate = (dateString: string) => {
    const date = parseISO(dateString);
    const formattedDate = format(date, "EEEE, dd/MM/yyyy", { locale: pt });
    return formattedDate;
};

export const getStatusChip = (status: "scheduled" | "created") => {
    if (status === "created") {
        return <Chip label="Criado" color="success" size="small" />;
    }
    return <Chip label="Agendado" color="warning" size="small" />;
};

export const calculateUnitPrice = (item: OrderItemForScheduling) => {
    const unitPriceAfterDiscount = item.price - (item.price * item.discount / 100);
    return unitPriceAfterDiscount;
};

export const generateScheduleItemsFromOrderItems = (orderItems: Array<OrderItemForScheduling>): Array<ScheduledInvoiceItem> => {
    const items: Array<ScheduledInvoiceItem> = [];
    
    orderItems.forEach((orderItem) => {
        const unitPrice = calculateUnitPrice(orderItem);
        
        for (let i = 0; i < orderItem.quantity; i++) {
            items.push({
                service: orderItem.serviceId,
                quantity: 1,
                price: unitPrice,
            });
        }
    });
    
    return items;
};

export const calculateTotalInvoicesCount = (orderItems: Array<OrderItemForScheduling>) => {
    const totalCount = orderItems.reduce((sum, item) => sum + item.quantity, 0);
    return totalCount;
};

export const toLocalISOString = (date: Date) => {
    const formatted = format(date, "yyyy-MM-dd'T'HH:mm:ss");
    return formatted;
};



